import { useCallback, useEffect, useRef, useState } from "react";
import type { OccurrenceChatMessage, OccurrenceChatPageResponse } from "@edu/shared";
import { ApiError } from "../../lib/api.ts";
import { nextAskMs } from "../../lib/poll.ts";
import { useChatDrafts } from "./use-chat-drafts.ts";
import {
  deleteOccurrenceChatMessage,
  getOccurrenceChatPage,
  markOccurrenceChatRead,
  sendOccurrenceChatMessage,
} from "./occurrence-chat-api.ts";

/**
 * Лента чата занятия, опрос её свежести, отправка и удаление: всё, что делают
 * ОДИНАКОВО все три места показа.
 *
 * Проект целиком в `docs/room-chat-design.md`. Мест три: панель комнаты,
 * всплывающая карточка занятия в сетке расписания и строка списка уроков. Свои
 * у них раскладка и слова пустой ленты, а работа с сетью общая, и второе её
 * написание разошлось бы с первым молча.
 *
 * ⚠️ **Опрашивается СТРАНИЦА, а не дешёвый счётчик, и это вынужденно.** Счётчик
 * (`/v1/occurrence-chats/unread`) сделан ровно под опрос и весит три поля на
 * занятие, но отвечает он адресом занятия ГЛАЗАМИ СМОТРЯЩЕГО и говорит только
 * «сколько нового», а открытой ленте нужны сами слова. Лимит частоты у страницы
 * выставлен под этот опрос (240 в минуту по сессии), и в шапке ручки это
 * записано словами. Точку в расписании при этом считает именно счётчик, своим
 * хуком (`use-chat-unread.ts`): тридцать открытых лент в неделе не бывает.
 *
 * ⚠️ **Цена названа вслух: страница это дюжина сообщений**, то есть в худшем
 * случае около 14 КБ сжатых на запрос, против 1,3 КБ у счётчика. Поэтому
 * закрытая панель спрашивает втрое реже открытой: за ней стоит одна точка на
 * кнопке, и пятнадцать секунд опоздания у точки стоят меньше, чем у ленты,
 * которую человек читает.
 *
 * ⚠️ **В комнате это первый УЧЕНИЧЕСКИЙ опрос**: до сих пор там спрашивал только
 * преподаватель (отметки «не понимаю», разбор в `lib/poll.ts`). Из этого следует
 * единственное требование, и оно жёсткое: отказ опроса не имеет права стать
 * заметным на уроке. Он проглатывается молча, уводит опрос в отступ и оставляет
 * на экране последнюю приехавшую ленту; красная строка появляется ровно в одном
 * случае, когда показать нечего вовсе.
 *
 * Четыре правила опроса живут в чистой `nextAskMs` (пауза на скрытой вкладке,
 * отступ при обрыве, отсчёт от ответа), а флаг «без нахлёста» и дедупликация по
 * подписи стоят здесь: их в чистую функцию не выносили намеренно, они про
 * порядок вызовов и про перерисовку, а не про сроки.
 */

/**
 * Во сколько раз реже спрашивает закрытая панель. Разбор в шапке.
 *
 * Умножается только ЗДОРОВЫЙ период: отступ при обрыве уже растёт сам, и
 * умножать его второй раз значило бы получить шестиминутную паузу там, где
 * задумана двухминутная.
 */
const CLOSED_EVERY = 3;

/**
 * Состояние чата, из которого каждое место собирает СВОЙ вид.
 *
 * ⚠️ Вид собирается по местам, а не здесь: у комнаты в нём кнопка дока с
 * точкой, у карточки заголовок и четыре пустых состояния по роли, и общего у
 * этих двух видов ровно столько, сколько уже вынесено в `chat-text.ts`.
 */
export interface OccurrenceChatState {
  page: {
    scope: "pair" | "group";
    canWrite: boolean;
    items: readonly { createdAt: number; mine: boolean }[];
  } | null;
  /** Сервер ответил «такого чата нет»: погашенный рычаг либо скрытый состав. */
  absent: boolean;
  /** Последний запрос не удался. */
  failed: boolean;
  /** До какого момента человек уже видел ленту. Разбор у `seenAt` ниже. */
  seenAt: number;
}

export interface OccurrenceChat {
  state: OccurrenceChatState;
  /** Сообщения в ХРОНОЛОГИЧЕСКОМ порядке: сверху старое, снизу свежее. */
  items: readonly OccurrenceChatMessage[];
  /** Есть ли более ранние страницы. */
  hasOlder: boolean;
  /** Идёт догрузка прошлого. */
  olderBusy: boolean;
  /**
   * Догрузка прошлого не удалась.
   *
   * ⚠️ **Отдельный признак от `failed` у опроса, и это не дубль.** Отказ опроса
   * человек не просил и знать о нём не должен: лента просто не меняется. А
   * догрузку он попросил нажатием, и кнопка, которая на нажатие не отвечает
   * ничем, читается как поломка. Молчаливая деградация запрещена разделом 5.5
   * CLAUDE.md прямо.
   */
  olderFailed: boolean;
  loadOlder: () => void;
  /** Спросить ленту заново. Висит на кнопке «Повторить». */
  retry: () => void;
  /** Отправить. Бросает `ApiError`: слова для человека собирает форма. */
  send: (body: string) => Promise<void>;
  /** Убрать сообщение. Бросает `ApiError` по той же причине, что отправка. */
  remove: (messageId: string) => Promise<void>;
  /** Набранное и не отправленное. Разбор целиком в `use-chat-drafts.ts`. */
  draft: string;
  setDraft: (value: string) => void;
}

/**
 * Подпись ответа: всё, из-за чего ленте стоит перерисоваться.
 *
 * ⚠️ Время удаления входит в подпись наравне с идентификатором: убранное
 * сообщение остаётся в ленте надгробием, то есть список идентификаторов у него
 * не меняется, и без второй половины подписи надгробие не появилось бы до
 * первого нового сообщения.
 */
function signatureOf(res: OccurrenceChatPageResponse): string {
  return `${String(res.canWrite)}|${res.items
    .map((m) => `${m.id}:${String(m.deletedAt ?? 0)}`)
    .join(",")}`;
}

/**
 * `address` это `null`, когда ленты на экране нет вовсе: свёрнутая строка
 * списка уроков, закрытая карточка занятия.
 *
 * ⚠️ **Условие уезжает параметром, а не ветвлением вокруг вызова**: состав
 * хуков обязан совпадать от рендера к рендеру. Ради этого же хук зовётся один
 * раз на весь список уроков открытой строкой, а не по разу на строку: двести
 * строк с собственным таймером это двести запросов на пустом месте.
 */
export function useOccurrenceChat(args: {
  address: string | null;
  open: boolean;
}): OccurrenceChat {
  const address = args.address;

  const [items, setItems] = useState<readonly OccurrenceChatMessage[]>([]);
  /** Шапка страницы: вид занятия и окно письма. `null` означает «ещё не было». */
  const [head, setHead] = useState<{ scope: "pair" | "group"; canWrite: boolean } | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [absent, setAbsent] = useState(false);
  const [failed, setFailed] = useState(false);
  const [olderBusy, setOlderBusy] = useState(false);
  const [olderFailed, setOlderFailed] = useState(false);
  /**
   * До какого момента человек видел ленту в этот заход.
   *
   * ⚠️ **Указатель МЕСТНЫЙ, а не серверный, и это вынужденно.** Серверная
   * отметка прочтения существует, но обратно её не отдаёт ни одна из пяти ручек:
   * страница чата её не возит, а дешёвый счётчик отвечает адресом занятия и
   * числом, а не моментом последнего просмотра. Ноль в начале означает, что
   * лежавшее в чате до открытия считается непоказанным: ошибка в сторону «лучше
   * покажем», и стоит она одного лишнего нажатия.
   */
  const [seenAt, setSeenAt] = useState(0);
  /** Набранное и не отправленное. Живёт по адресам, разбор в `use-chat-drafts.ts`. */
  const drafts = useChatDrafts();

  /**
   * Прошлое уже догружали.
   *
   * ⚠️ Нужен ровно затем, чтобы опрос не сбрасывал курсор назад: свежая страница
   * приезжает со своим `nextCursor`, указывающим на границу ПЕРВОЙ дюжины, и
   * приняв его после догрузки, кнопка «более ранние» показывала бы уже
   * показанное по кругу.
   */
  const pagedBack = useRef(false);
  /** Живая ручка «спросить сейчас»: её переставляет эффект опроса. */
  const askNow = useRef<() => void>(() => {});
  /** Открыта ли лента, для опроса. Через ref: перезапускать таймер незачем. */
  const opened = useRef(args.open);
  useEffect(() => {
    opened.current = args.open;
  });

  /**
   * Слияние приехавшей страницы с показанным.
   *
   * ⚠️ **Замена списка целиком не годится:** к свежей дюжине могло быть
   * догружено прошлое, и замена стёрла бы его под пальцем у того, кто как раз
   * его листает. Поэтому из показанного выбрасывается всё, что приехало заново,
   * и свежее приписывается снизу: ничего не теряется и ничего не двоится.
   *
   * ⚠️ Известная дыра названа: если между двумя заходами опроса придёт больше
   * дюжины сообщений, середина в ленту не попадёт. Это тринадцать сообщений за
   * пять секунд, то есть не разговор, и лечится оно кнопкой «более ранние».
   */
  const apply = useCallback((res: OccurrenceChatPageResponse) => {
    const fresh = [...res.items].reverse();
    const ids = new Set(fresh.map((m) => m.id));
    setItems((prev) => [...prev.filter((m) => !ids.has(m.id)), ...fresh]);
    setHead({ scope: res.scope, canWrite: res.canWrite });
    if (!pagedBack.current) setCursor(res.nextCursor);
  }, []);

  useEffect(() => {
    /*
      ⚠️ Смена адреса ОБНУЛЯЕТ ленту, и это не уборка, а починка. В комнате
      адрес один на весь урок, а в расписании карточки переключаются нажатием по
      сетке: без обнуления сообщения вторника доезжали бы в карточку среды и
      висели там до первого ответа сервера. Черновики при этом не трогаются, они
      лежат по адресам.
    */
    setItems([]);
    setHead(null);
    setCursor(null);
    setAbsent(false);
    setFailed(false);
    setSeenAt(0);
    pagedBack.current = false;
    if (address === null) return;

    let alive = true;
    let timer: number | null = null;
    let running = false;
    let fails = 0;
    let seen = "";
    /** Чата нет вовсе: спрашивать больше нечего и незачем. */
    let gone = false;

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const plan = (ms: number | null) => {
      clearTimer();
      if (!alive || gone || ms === null) return;
      timer = window.setTimeout(() => void ask(), ms);
    };

    const ask = async () => {
      timer = null;
      // Ни одного запроса внахлёст: медленный ответ, наложившись на свежий,
      // вернул бы в ленту уже убранное сообщение
      if (!alive || gone || running) return;
      if (document.visibilityState === "hidden") return;

      running = true;
      try {
        const res = await getOccurrenceChatPage(address);
        if (!alive) return;
        fails = 0;
        setFailed(false);
        const next = signatureOf(res);
        // Перерисовка только по изменению: ответ это новый объект, и класть его
        // в состояние двенадцать раз в минуту значило бы перерисовывать ленту
        // поверх набранного и не отправленного текста
        if (next !== seen) {
          seen = next;
          apply(res);
        }
      } catch (e) {
        if (!alive) return;
        /*
          ⚠️ 404 это «чата у этого занятия нет», а не «не доехало». Так выглядит
          группа с погашенным рычагом либо со скрытым составом, и повторять такой
          запрос незачем: лента просто исчезает вместе с точкой входа.
        */
        if (e instanceof ApiError && e.status === 404) {
          gone = true;
          setAbsent(true);
          clearTimer();
          return;
        }
        /*
          Всё остальное проглатывается молча, и лента просто не меняется. Красная
          строка поверх урока была бы хуже устаревшего списка: то же правило, что
          у отметок «не понимаю» и у счётчика переписки. Видимой она становится
          ровно тогда, когда показать вместо неё нечего.
        */
        fails += 1;
        setFailed(true);
      } finally {
        running = false;
        if (alive && !gone) {
          const ms = nextAskMs({ visible: document.visibilityState === "visible", fails });
          plan(ms === null || fails > 0 || opened.current ? ms : ms * CLOSED_EVERY);
        }
      }
    };

    const wake = () => {
      if (document.visibilityState === "visible") {
        // Вернувшемуся человеку лента нужна сейчас, а не через пять секунд, и
        // отступ обнуляется вместе с этим: сеть могла починиться, пока он ушёл
        fails = 0;
        plan(0);
      } else {
        clearTimer();
      }
    };

    askNow.current = () => {
      fails = 0;
      plan(0);
    };
    document.addEventListener("visibilitychange", wake);
    void ask();

    return () => {
      alive = false;
      clearTimer();
      document.removeEventListener("visibilitychange", wake);
      askNow.current = () => {};
    };
  }, [address, apply]);

  /* Ленту открыли: она нужна свежей сейчас, а не через пятнадцать секунд */
  const open = args.open;
  useEffect(() => {
    if (open) askNow.current();
  }, [open]);

  /**
   * Человек смотрит на ленту, значит дочитал до свежего края.
   *
   * ⚠️ Отметка ставится ЗДЕСЬ и только здесь, и отказ её проглатывается молча:
   * это наша запись о работе человека, а не его работа, и красная строка о её
   * неудаче сообщала бы о поломке, которой для его дела нет.
   */
  const newest = items.at(-1)?.createdAt ?? 0;
  useEffect(() => {
    if (address === null || !open || newest <= seenAt) return;
    setSeenAt(newest);
    void markOccurrenceChatRead(address).catch(() => undefined);
  }, [address, open, newest, seenAt]);

  const loadOlder = useCallback(() => {
    if (address === null || cursor === null || olderBusy) return;
    setOlderBusy(true);
    setOlderFailed(false);
    void (async () => {
      try {
        const res = await getOccurrenceChatPage(address, cursor);
        const older = [...res.items].reverse();
        setItems((prev) => {
          const have = new Set(prev.map((m) => m.id));
          return [...older.filter((m) => !have.has(m.id)), ...prev];
        });
        pagedBack.current = true;
        setCursor(res.nextCursor);
      } catch {
        // Про это человек обязан узнать: он нажал, и ничего не произошло
        setOlderFailed(true);
      } finally {
        setOlderBusy(false);
      }
    })();
  }, [address, cursor, olderBusy]);

  const send = useCallback(
    async (body: string) => {
      if (address === null) return;
      await sendOccurrenceChatMessage(address, body);
      drafts.clear(address);
      // Своё сообщение приезжает той же страницей, что чужие: второго места
      // сборки строки в проекте нет (разбор у самой ручки)
      askNow.current();
    },
    [address, drafts],
  );

  const remove = useCallback(
    async (messageId: string) => {
      if (address === null) return;
      await deleteOccurrenceChatMessage(address, messageId);
      // Надгробие приезжает страницей, а не подставляется здесь: состояний у
      // сообщения три, и второе место их сборки разошлось бы с первым
      askNow.current();
    },
    [address],
  );

  const retry = useCallback(() => askNow.current(), []);

  const setDraft = useCallback(
    (value: string) => {
      drafts.setDraft(address, value);
    },
    [address, drafts],
  );

  return {
    /*
      Состояние собирается на каждый рендер и не запоминается: `useMemo` тут не
      сработал бы ни разу (свежий объект он сравнивал бы сам с собой), а
      запоминать нечего, потому что ниже нет ни одного `memo`.
    */
    state: { page: head === null ? null : { ...head, items }, absent, failed, seenAt },
    items,
    hasOlder: cursor !== null,
    olderBusy,
    olderFailed,
    loadOlder,
    retry,
    send,
    remove,
    draft: drafts.draftOf(address),
    setDraft,
  };
}
